class WishlistProductGrid extends HTMLElement {
  static WISHLIST_CACHE = "no-cache";
  static SWATCH_DISPLAY_LIMIT = 4;
  static VARIANT_MEDIA_MAX = 1800;

  constructor() {
    super();

    this.cardConfig = {
      showQuickBuy: this.getAttribute("data-show-quick-buy") === "true",
      showSizeSelector: this.getAttribute("data-show-size-selector") === "true",
      showSwatches: this.getAttribute("data-show-swatches") === "true",
      swatchesTight: this.getAttribute("data-swatches-tight") === "true",
      swatchesPosition: this.getAttribute("data-swatches-position") || "outside",
      swatchesPlacement: this.getAttribute("data-swatches-placement") || "",
      colorSwatchShape: this.getAttribute("data-color-swatch-shape") || "circle",
      sizeLabels: this.parseCommaList(this.getAttribute("data-size-labels") || "", false),
      colorLabels: this.parseCommaList(this.getAttribute("data-color-labels") || "", true),
      whiteLabel: (this.getAttribute("data-white-label") || "white").toLowerCase().trim(),
      chooseOptions: this.getAttribute("data-choose-options") || "Choose options",
      addToCart: this.getAttribute("data-add-to-cart") || "Add to cart",
      sectionId: this.getAttribute("data-section-id") || "wishlist",
      buttonBg: this.getAttribute("data-button-bg") || "",
      buttonText: this.getAttribute("data-button-text") || "",
      defaultSchemeId: this.getAttribute("data-default-scheme-id") || "scheme-1",
      buttonAnimation: this.getAttribute("data-button-animation") || "",
      showBadges: this.getAttribute("data-show-badges") === "true",
      showCustomBadge: this.getAttribute("data-show-custom-badge") === "true",
      showSoldOutBadge: this.getAttribute("data-show-sold-out-badge") === "true",
      showDiscountBadge: this.getAttribute("data-show-discount-badge") === "true",
      discountMode: this.getAttribute("data-discount-mode") || "saving",
      badgesPlacement: this.getAttribute("data-badges-placement") || "place-self-start",
      badgesVerticalText: this.getAttribute("data-badges-vertical") === "true",
      badgesAlignment: this.getAttribute("data-badges-alignment") || "",
      soldOutLabel: this.getAttribute("data-sold-out-badge") || "Sold out",
      onSaleLabel: this.getAttribute("data-on-sale-badge") || "On sale",
      discountBadgeHtml: this.getAttribute("data-discount-badge-html") || "-{{ savings }}"
    };

    this.presentationAttrs = {
      primaryImageClasses:
        this.getAttribute("data-primary-image-classes") || "product-card__image product-card__image--primary aspect-square",
      secondaryImageClasses:
        this.getAttribute("data-secondary-image-classes") || "product-card__image product-card__image--secondary",
      vendorClass: this.getAttribute("data-vendor-class") || "text-sm text-subdued",
      titleClass: this.getAttribute("data-title-class") || "text-sm",
      priceClass: this.getAttribute("data-price-class") || "text-sm text-subdued"
    };

    this.cartIconHtml = "";
    this.heartIconHtml = "";
  }

  templateInnerHtml(template) {
    if (!template?.content) {
      return "";
    }
    const wrap = document.createElement("div");
    wrap.appendChild(template.content.cloneNode(true));
    return wrap.innerHTML;
  }

  readWishlistIconsFromTemplates() {
    const cartTpl = this.querySelector('template[data-wishlist-icon="cart"]');
    const heartTpl = this.querySelector('template[data-wishlist-icon="heart"]');
    if (cartTpl) {
      this.cartIconHtml = this.templateInnerHtml(cartTpl);
    }
    if (heartTpl) {
      this.heartIconHtml = this.templateInnerHtml(heartTpl);
    }
  }

  connectedCallback() {
    this.readWishlistIconsFromTemplates();
    this.renderLoading();
    if (!window.WishlistKing) {
      document.addEventListener("wk:app:ready", () => this.loadWishlist());
    } else {
      this.loadWishlist();
    }
  }

  async loadWishlist() {
    try {
      await WishlistKing.apis.wishlist.clearCache(true);
      const { wishlist } = await WishlistKing.apis.wishlist.loadWishlist({
        wishlistId: "mine",
        cacheStrategy: WishlistProductGrid.WISHLIST_CACHE
      });
      this.renderWishlistProducts(wishlist.items);
      this.subscribeToWishlistUpdates();
    } catch (error) {
      console.error("[Wishlist] Failed to load wishlist data:", error);
      this.renderMessage("Deine Wunschliste konnte gerade nicht geladen werden.");
    }
  }

  subscribeToWishlistUpdates() {
    if (this.wishlistSubscription) {
      // Avoid multiple subscriptions
      return;
    }
    this.wishlistSubscription = WishlistKing.state
      .observeWishlist({
        wishlistId: "mine",
      })
      .subscribe((wishlist) => {
        console.log("[Wishlist] wishlist updated:", wishlist);
        this.renderWishlistProducts(wishlist.items);
      });
  }

  renderWishlistProducts(data) {
    const nextData = JSON.stringify(data);
    if (this.lastRenderedData === nextData) {
      return;
    }
    this.lastRenderedData = nextData;

    const products = Array.isArray(data) ? data.map((entry) => this.normalizeWishlistItem(entry)).filter(Boolean) : [];
    if (!products.length) {
      this.lastRenderedData = "";
      this.renderMessage("Deine Wunschliste ist leer.");
      return;
    }

    const staggerReveal = window.themeVariables?.settings?.staggerProducts === true;
    const isFirstRender = !this.querySelector("#wishlist-product-list");

    const pres = {
      ...this.presentationAttrs,
      staggerReveal: isFirstRender ? staggerReveal : false
    };
    const cardsMarkup = products
      .map((product, index) =>
        this.renderProductCard(product, {
          ...pres,
          cardConfig: this.cardConfig,
          cardIndex: index
        })
      )
      .join("");

    this.innerHTML = `
      <div class="w-full">
        <product-list id="wishlist-product-list" class="product-list justify-center">
          ${cardsMarkup}
        </product-list>
      </div>`;
  }

  renderLoading() {
    this.renderMessage("Bitte warten...");
  }

  renderMessage(message) {
    this.innerHTML = `<p class="text-center text-subdued">${this.escapeHtml(message)}</p>`;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  parseCommaList(csv, downcase) {
    if (!csv || typeof csv !== "string") {
      return [];
    }
    return csv
      .split(",")
      .map((s) => (downcase ? s.trim().toLowerCase() : s.trim()))
      .filter(Boolean);
  }

  normalizeWishlistItem(item) {
    const product = item?.product ?? item?.shopify_product ?? item;
    if (!product || typeof product !== "object" || product.hidden === true) {
      return null;
    }

    const handle = product.handle || item?.product_handle || item?.handle || "";
    const title = product.title || product.name || item?.title || "Untitled product";
    const url = product.url || item?.product_url || (handle ? `/products/${handle}` : "#");
    const image = this.extractImageUrl(product, item);
    const secondaryImage = this.extractSecondaryImageUrl(product, item, image);
    const vendor = product.vendor || item?.vendor || "";
    const price = this.extractPrice(product, item);
    const compareAtPrice = this.extractCompareAtPrice(product, item);
    const onSale = this.isProductOnSale(product, item);
    const isWishlistEntry = item?.product != null && typeof item.product === "object";
    const wishlistItemId =
      isWishlistEntry && item?.id != null ? (typeof item.id === "string" ? item.id : String(item.id)) : "";

    return {
      wishlistItemId,
      handle,
      title,
      url,
      image,
      secondaryImage,
      vendor,
      price,
      compareAtPrice,
      onSale,
      numericProductId: product.id != null ? String(product.id) : "",
      variants: Array.isArray(product.variants) ? product.variants : [],
      options: Array.isArray(product.options) ? product.options : [],
      available: product.available !== false,
      sellingPlanGroups: Array.isArray(product.selling_plan_groups) ? product.selling_plan_groups : [],
      compareAtPriceVaries: product.compare_at_price_varies === true,
      customBadges: this.extractCustomBadges(product),
      rawProduct: product
    };
  }

  extractCustomBadges(product) {
    const badges = product?.metafields?.custom?.badges;
    if (badges == null || badges === "") {
      return [];
    }

    if (Array.isArray(badges)) {
      return badges.map((badge) => String(badge).trim()).filter(Boolean);
    }

    // Wishlist API returns list metafields as a JSON-encoded string, e.g.
    // { "custom": { "badges": "[\"New arrival\"]" } }
    if (typeof badges === "string") {
      const trimmed = badges.trim();
      if (!trimmed) {
        return [];
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((badge) => String(badge).trim()).filter(Boolean);
        }
        if (typeof parsed === "string" && parsed.trim()) {
          return [parsed.trim()];
        }
      } catch {
        /* invalid JSON — no badges */
      }
      return [];
    }

    return [];
  }

  handleize(value) {
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  firstImageUrlFromImages(images) {
    if (!Array.isArray(images) || !images.length) {
      return "";
    }
    const first = images[0];
    if (typeof first === "string") {
      return first;
    }
    if (first && typeof first === "object") {
      return first.src || first.url || "";
    }
    return "";
  }

  extractImageUrl(product, item) {
    const featured = product?.featured_image;
    const candidates = [
      typeof featured === "string" ? featured : "",
      featured?.src,
      featured?.url,
      product?.featuredImage?.url,
      product?.image?.src,
      product?.image?.url,
      item?.image?.src,
      item?.image?.url,
      item?.image,
      this.firstImageUrlFromImages(product?.images),
      product?.images?.[0]?.src,
      product?.images?.[0]?.url,
      Array.isArray(product?.variants) ? product.variants[0]?.featured_image?.src : ""
    ];
    return candidates.find((c) => typeof c === "string" && c.length > 0) || "";
  }

  extractSecondaryImageUrl(product, item, primaryImageUrl) {
    const images = product?.images;
    let second = "";
    if (Array.isArray(images) && images.length > 1) {
      const raw = images[1];
      second = typeof raw === "string" ? raw : raw?.src || raw?.url || "";
    }
    const candidates = [second, product?.images?.[1]?.src, product?.images?.[1]?.url, item?.images?.[1]?.src, item?.images?.[1]?.url];
    const found = candidates.find((c) => typeof c === "string" && c.length > 0);
    return found && found !== primaryImageUrl ? found : "";
  }

  extractPrice(product, item) {
    const firstAvailableVariant = this.getFirstAvailableVariant(product);
    const priceRaw =
      product.price_min ??
      product.price_max ??
      product.price ??
      firstAvailableVariant?.price ??
      item?.price ??
      item?.variant?.price ??
      null;
    return this.formatPriceFromRaw(priceRaw);
  }

  extractCompareAtPrice(product, item) {
    const firstAvailableVariant = this.getFirstAvailableVariant(product);
    const compareRaw =
      product.compare_at_price_min ??
      product.compare_at_price_max ??
      product.compare_at_price ??
      firstAvailableVariant?.compare_at_price ??
      item?.compare_at_price ??
      item?.variant?.compare_at_price ??
      null;
    return this.formatPriceFromRaw(compareRaw);
  }

  normalizeMoneyCents(priceRaw) {
    if (priceRaw === null || priceRaw === undefined || priceRaw === "") {
      return null;
    }
    if (typeof priceRaw === "string" && /[A-Za-z]/.test(priceRaw)) {
      return null;
    }

    const numericPrice = Number(priceRaw);
    if (Number.isNaN(numericPrice)) {
      return null;
    }

    const rawString = String(priceRaw).trim();
    const rounded = Math.round(numericPrice);
    const isWholeMoneyNumber = Math.abs(numericPrice - rounded) < 0.02;

    if (/^\d+$/.test(rawString)) {
      return rounded;
    }
    if (typeof priceRaw === "number" && isWholeMoneyNumber && rounded >= 100) {
      return rounded;
    }

    return Math.round(numericPrice * 100);
  }

  isProductOnSale(product, item) {
    const firstAvailableVariant = this.getFirstAvailableVariant(product);
    const priceCents = this.normalizeMoneyCents(
      product?.price_min ??
        product?.price_max ??
        product?.price ??
        firstAvailableVariant?.price ??
        item?.price ??
        item?.variant?.price
    );
    const compareCents = this.normalizeMoneyCents(
      product?.compare_at_price_min ??
        product?.compare_at_price_max ??
        product?.compare_at_price ??
        firstAvailableVariant?.compare_at_price ??
        item?.compare_at_price ??
        item?.variant?.compare_at_price
    );
    return compareCents != null && priceCents != null && compareCents > priceCents;
  }

  formatPriceFromRaw(priceRaw) {
    if (priceRaw === null || priceRaw === undefined || priceRaw === "") {
      return "";
    }
    if (typeof priceRaw === "string" && /[A-Za-z]/.test(priceRaw)) {
      return priceRaw;
    }

    const cents = this.normalizeMoneyCents(priceRaw);
    if (cents === null) {
      return String(priceRaw);
    }

    return this.formatMoney(cents / 100);
  }

  derivePriceClasses(priceClass) {
    const regular = priceClass || "text-sm text-subdued";
    const onSale = regular.includes("text-subdued") ? regular.replace("text-subdued", "text-on-sale") : `${regular} text-on-sale`;
    const compareAt = regular.includes("line-through")
      ? regular
      : `${regular.replace(/\btext-on-sale\b/g, "text-subdued")} line-through`.replace(/\s+/g, " ").trim();
    return { regular, onSale, compareAt };
  }

  formatMoney(amount) {
    const format = window.themeVariables?.settings?.moneyWithCurrencyFormat || "CHF {{amount}}";
    const normalized = Number(amount);
    if (Number.isNaN(normalized)) {
      return String(amount);
    }

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      const [whole, fraction = ""] = number.toFixed(precision).split(".");
      const withThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision > 0 ? `${withThousands}${decimal}${fraction}` : withThousands;
    };

    const value = formatWithDelimiters(normalized, 2, ",", ".");

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  variantSeoHidden(variant) {
    const seo = variant?.metafields?.seo;
    if (seo && typeof seo === "object" && "hidden" in seo) {
      return seo.hidden === true || seo.hidden === "true";
    }
    return false;
  }

  getFirstAvailableVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const available = variants.find((v) => v && !this.variantSeoHidden(v) && v.available !== false);
    if (available) {
      return available;
    }
    const visible = variants.find((v) => v && !this.variantSeoHidden(v));
    return visible ?? variants[0] ?? null;
  }

  getVisibleVariants(card) {
    return card.variants.filter((v) => v && !this.variantSeoHidden(v));
  }

  optionNameMatchesSizeLabels(optionName, labels) {
    if (!optionName || !labels.length) {
      return false;
    }
    const n = optionName.trim().toLowerCase();
    return labels.some((label) => label.toLowerCase() === n);
  }

  shouldShowFloatingSizeSelector(card, config) {
    return config.showSizeSelector && card.options.length === 1 && this.optionNameMatchesSizeLabels(card.options[0].name, config.sizeLabels);
  }

  getVariantOptionValue(variant, optionIndex) {
    const keys = ["option1", "option2", "option3"];
    return variant?.[keys[optionIndex]] ?? null;
  }

  findFirstRepresentativeVariantForOptionValue(card, optionIndex, valueName) {
    return this.getVisibleVariants(card).find((v) => {
      const val = this.getVariantOptionValue(v, optionIndex);
      return val != null && String(val) === String(valueName);
    });
  }

  variantMediaPayloadFromVariant(variant, rawProduct) {
    const src = variant?.featured_image?.src || this.extractImageUrl(rawProduct, {});
    if (!src) {
      return null;
    }
    return {
      alt: variant?.featured_image?.alt || "",
      preview_image: { src, width: WishlistProductGrid.VARIANT_MEDIA_MAX, height: WishlistProductGrid.VARIANT_MEDIA_MAX }
    };
  }

  secondaryMediaPayloadForVariant(variant, rawProduct) {
    const primarySrc = variant?.featured_image?.src || this.firstImageUrlFromImages(rawProduct?.images);
    const images = rawProduct?.images;
    if (!Array.isArray(images) || images.length < 2) {
      return null;
    }
    const raw = images[1];
    const second = typeof raw === "string" ? raw : raw?.src || raw?.url || "";
    if (!second || second === primarySrc) {
      return null;
    }
    return {
      alt: "",
      preview_image: { src: second, width: WishlistProductGrid.VARIANT_MEDIA_MAX, height: WishlistProductGrid.VARIANT_MEDIA_MAX }
    };
  }

  colorOptionForCard(card, config) {
    if (!config.showSwatches || !card.options.length || !config.colorLabels.length) {
      return null;
    }
    for (let i = 0; i < card.options.length; i++) {
      const name = (card.options[i].name || "").trim().toLowerCase();
      if (config.colorLabels.includes(name)) {
        return { option: card.options[i], index: i };
      }
    }
    return null;
  }

  swatchStyleForValue(valueName) {
    const down = String(valueName).toLowerCase().trim();
    return `--swatch-background: linear-gradient(to right, ${down}, ${down})`;
  }

  cartAddAction() {
    const root = window.Shopify?.routes?.root || "/";
    return `${root}cart/add`;
  }

  buttonInlineStyle(config) {
    if (!config.buttonBg || !config.buttonText) {
      return "";
    }
    return ` style="--button-background: ${this.escapeHtml(config.buttonBg)};--button-text-color: ${this.escapeHtml(config.buttonText)};"`;
  }

  buttonContentAnimationAttrs(content, animation) {
    const mode = (animation || "").trim();
    const escaped = this.escapeHtml(content);
    if (mode === "rotation") {
      return ` rotated-text="${escaped}"`;
    }
    if (mode === "up") {
      return ` up-text="${escaped}"`;
    }
    if (mode === "glowing") {
      return " glowing";
    }
    return "";
  }

  renderProductFormFields(variantId) {
    return `
    <input type="hidden" name="form_type" value="product">
    <input type="hidden" name="utf8" value="✓">
    <input type="hidden" name="quantity" value="1">
    <input type="hidden" name="id" value="${this.escapeHtml(String(variantId))}">
  `;
  }

  renderFloatingSizeSelector(card, config, uniqueSuffix) {
    const visible = this.getVisibleVariants(card);
    if (!visible.length) {
      return "";
    }

    const formAction = this.escapeHtml(this.cartAddAction());
    const btnStyle = this.buttonInlineStyle(config);

    const items = visible
      .map((variant) => {
        const disabled = variant.available === false;
        const disabledClass = disabled ? " is-disabled" : "";
        const disabledAttr = disabled ? " disabled" : "";
        const formId = `wishlist-size-form-${uniqueSuffix}-${variant.id}`;
        const label = variant.title || String(variant.option1 || "");
        const animAttrs = this.buttonContentAnimationAttrs(label, config.buttonAnimation);
        return `
        <product-form>
          <form method="post" action="${formAction}" id="${this.escapeHtml(formId)}" accept-charset="UTF-8" enctype="multipart/form-data" class="shopify-product-form">
            ${this.renderProductFormFields(variant.id)}
            <button type="submit"${btnStyle} class="floating-size-selector__button button button--sm w-full${disabledClass}"${disabledAttr}>
              <button-content class="button__content"${animAttrs}>${this.escapeHtml(label)}</button-content>
            </button>
          </form>
        </product-form>`;
      })
      .join("");

    return `<div class="product-card__floating-size-selector touch:hidden">${items}</div>`;
  }

  renderQuickBuy(card, config, uniqueSuffix, showFloatingSize) {
    if (!config.showQuickBuy || !card.available) {
      return "";
    }

    const visible = this.getVisibleVariants(card);
    if (!visible.length) {
      return "";
    }

    const totalVariantCount = Array.isArray(card.rawProduct?.variants) ? card.rawProduct.variants.length : visible.length;
    const sellingPlanCount = card.sellingPlanGroups.length;
    const singleVariantPath = totalVariantCount === 1 && sellingPlanCount === 0;

    const quickBuyId = `wishlist-quick-buy-${uniqueSuffix}`;
    const formAction = this.escapeHtml(this.cartAddAction());
    const btnStyle = this.buttonInlineStyle(config);
    const quickBuyHiddenClass = showFloatingSize ? " no-touch:hidden" : "";
    const schemeClass = this.escapeHtml(config.defaultSchemeId);
    const productUrlAttr = this.escapeHtml(card.url.split("?")[0]);

    let inner;
    if (singleVariantPath) {
      const v = visible[0];
      const soldOut = v.available === false;
      const disabledAttr = soldOut ? " disabled" : "";
      const disabledClass = soldOut ? " opacity-50" : "";
      const addAttrs = this.buttonContentAnimationAttrs(config.addToCart, config.buttonAnimation);
      inner = `
      <product-form>
        <form method="post" action="${formAction}" accept-charset="UTF-8" enctype="multipart/form-data" class="shopify-product-form">
          ${this.renderProductFormFields(v.id)}
          <button type="submit"${btnStyle} class="button w-full touch:hidden${disabledClass}"${disabledAttr}>
            <button-content class="button__content"${addAttrs}>${this.escapeHtml(config.addToCart)}</button-content>
          </button>
          <button type="submit" class="block tap-area no-touch:hidden${disabledClass}"${disabledAttr}>
            <span class="sr-only">${this.escapeHtml(config.addToCart)}</span>
            <button-content>${this.cartIconHtml}</button-content>
          </button>
        </form>
      </product-form>`;
    } else {
      const chooseAttrs = this.buttonContentAnimationAttrs(config.chooseOptions, config.buttonAnimation);
      const chooseBlock = showFloatingSize
        ? ""
        : `<button type="button"${btnStyle} class="button w-full touch:hidden" aria-controls="${this.escapeHtml(quickBuyId)}">
            <button-content class="button__content"${chooseAttrs}>${this.escapeHtml(config.chooseOptions)}</button-content>
          </button>`;
      inner = `
      ${chooseBlock}
      <button type="button" aria-controls="${this.escapeHtml(quickBuyId)}" class="block tap-area no-touch:hidden">
        <span class="sr-only">${this.escapeHtml(config.chooseOptions)}</span>
        <button-content>${this.cartIconHtml}</button-content>
      </button>
      <quick-buy-modal id="${this.escapeHtml(quickBuyId)}" product-url="${productUrlAttr}" class="modal modal--quick-buy color-scheme color-scheme--${schemeClass}"></quick-buy-modal>`;
    }

    return `<div class="product-card__quick-buy${quickBuyHiddenClass}">${inner}</div>`;
  }

  renderColorSwatches(card, config, uniqueSuffix) {
    const colorPick = this.colorOptionForCard(card, config);
    if (!colorPick) {
      return "";
    }

    const { option, index: optionIndex } = colorPick;
    const values = Array.isArray(option.values) ? option.values : [];
    if (!values.length) {
      return "";
    }

    const shown = values.slice(0, WishlistProductGrid.SWATCH_DISPLAY_LIMIT);
    const remaining = values.length - shown.length;
    const paramName = `swatch-wishlist-${config.sectionId}-${uniqueSuffix}-${optionIndex + 1}`;
    const shape = config.swatchesTight ? "circle" : this.escapeHtml(config.colorSwatchShape);
    const tightClass = config.swatchesTight ? " color-swatch--tight" : "";
    const listPlacementClass =
      config.swatchesPosition === "inside" && config.swatchesPlacement ? ` ${this.escapeHtml(config.swatchesPlacement)}` : "";

    const radios = shown
      .map((valueName, valueIdx) => {
        const variant = this.findFirstRepresentativeVariantForOptionValue(card, optionIndex, valueName);
        const disabled = !variant || variant.available === false;
        const id = `wishlist-swatch-${uniqueSuffix}-${optionIndex}-${valueIdx}`;
        const media = variant ? this.variantMediaPayloadFromVariant(variant, card.rawProduct) : null;
        const secondary = variant ? this.secondaryMediaPayloadForVariant(variant, card.rawProduct) : null;
        const mediaAttr = media ? ` data-variant-media="${this.escapeHtml(JSON.stringify(media))}"` : "";
        const secAttr = secondary ? ` data-variant-secondary-media="${this.escapeHtml(JSON.stringify(secondary))}"` : "";
        const varIdAttr = variant ? ` data-variant-id="${this.escapeHtml(String(variant.id))}"` : "";
        const optPosAttr = ` data-option-position="${optionIndex + 1}"`;
        const disabledClass = disabled ? " is-disabled" : "";
        const disabledInput = disabled ? " disabled" : "";
        const whiteClass =
          config.whiteLabel && String(valueName).toLowerCase().trim() === config.whiteLabel ? " color-swatch--white" : "";
        const labelFor = this.escapeHtml(id);
        const style = this.swatchStyleForValue(valueName);
        const indicator = shape !== "circle" ? `<span class="color-swatch__indicator"></span>` : "";
        return `
        <input class="sr-only" type="radio" name="${this.escapeHtml(paramName)}" id="${labelFor}" value="${this.escapeHtml(String(valueName))}"${optPosAttr}${varIdAttr}${mediaAttr}${secAttr}${disabledInput}>
        <label for="${labelFor}" class="color-swatch color-swatch--${shape}${tightClass} color-swatch--sm${whiteClass}${disabledClass}" style="${this.escapeHtml(style)}">
          <span class="sr-only">${this.escapeHtml(String(valueName))}</span>
          ${indicator}
        </label>`;
      })
      .join("");

    const more =
      remaining > 0
        ? `<span class="${config.swatchesTight ? "color-swatch__view-more subheading" : "color-swatch__view-more subheading text-subdued"}">+${remaining}</span>`
        : "";

    return `
    <div class="product-card__swatch-list${listPlacementClass}">
      <fieldset class="h-stack gap-1.5 sm:gap-2">
        <legend class="sr-only">${this.escapeHtml(option.name || "Color")}</legend>
        ${radios}
        ${more}
      </fieldset>
    </div>`;
  }

  formatDiscountSavings(card, variant, discountMode) {
    const compareRaw = variant?.compare_at_price ?? card.rawProduct?.compare_at_price_min ?? card.rawProduct?.compare_at_price_max;
    const priceRaw = variant?.price ?? card.rawProduct?.price_min ?? card.rawProduct?.price_max;
    const compareCents = this.normalizeMoneyCents(compareRaw);
    const priceCents = this.normalizeMoneyCents(priceRaw);

    if (compareCents == null || priceCents == null || compareCents <= priceCents) {
      return "";
    }

    if (discountMode === "percentage") {
      return `${Math.round(((compareCents - priceCents) * 100.0) / compareCents)}%`;
    }

    return this.formatMoney((compareCents - priceCents) / 100);
  }

  renderProductBadges(card, config) {
    if (!config.showBadges) {
      return "";
    }

    const badges = [];
    const verticalClass = config.badgesVerticalText ? " vertical-text" : "";

    if (config.showCustomBadge) {
      for (const customBadge of card.customBadges) {
        const handle = this.handleize(customBadge);
        badges.push(
          `<span class="badge badge--custom badge--${this.escapeHtml(handle)}${verticalClass}">${this.escapeHtml(customBadge)}</span>`
        );
      }
    }

    if (config.showSoldOutBadge && !card.available) {
      badges.push(
        `<sold-out-badge class="badge badge--sold-out${verticalClass}">${this.escapeHtml(config.soldOutLabel)}</sold-out-badge>`
      );
    }

    if (config.showDiscountBadge && card.onSale) {
      const variant = this.getFirstAvailableVariant(card.rawProduct);
      const variantCompare = variant?.compare_at_price;
      const showGenericOnSale = card.compareAtPriceVaries || variantCompare == null || variantCompare === "";
      let label;

      if (showGenericOnSale) {
        label = this.escapeHtml(config.onSaleLabel);
      } else {
        const savings = this.escapeHtml(this.formatDiscountSavings(card, variant, config.discountMode));
        label = config.discountBadgeHtml.replace(/\{\{\s*savings\s*\}\}/g, savings);
      }

      badges.push(`<on-sale-badge class="badge badge--on-sale${verticalClass}">${label}</on-sale-badge>`);
    }

    if (!badges.length) {
      return "";
    }

    const direction = "column";
    const alignmentClass = config.badgesAlignment
      ? direction === "column"
        ? `align-${config.badgesAlignment}`
        : `justify-${config.badgesAlignment}`
      : "";
    const listClasses = [
      "badge-list",
      config.badgesVerticalText ? "" : `badge-list--${direction}`,
      alignmentClass,
      config.badgesPlacement
    ]
      .filter(Boolean)
      .join(" ");

    return `<badge-list class="${this.escapeHtml(listClasses)}">${badges.join("")}</badge-list>`;
  }

  renderPriceMarkup(product, priceClass) {
    if (!product.price) {
      return "";
    }

    const { regular, onSale, compareAt } = this.derivePriceClasses(priceClass);
    const saleCls = this.escapeHtml(product.onSale ? onSale : regular);
    const compareMarkup =
      product.onSale && product.compareAtPrice
        ? `<compare-at-price class="${this.escapeHtml(compareAt)}">${this.escapeHtml(product.compareAtPrice)}</compare-at-price>`
        : "";

    return `<price-list class="price-list bold">${compareMarkup}<sale-price class="${saleCls}">${this.escapeHtml(product.price)}</sale-price></price-list>`;
  }

  cardUniqueSuffix(card, cardConfig, cardIndex) {
    const base = `${cardConfig.sectionId}-${card.wishlistItemId || card.numericProductId || card.handle || "p"}-${cardIndex}`;
    return base.replace(/[^a-zA-Z0-9_-]/g, "");
  }

  renderProductCard(product, opts) {
    const {
      primaryImageClasses,
      secondaryImageClasses,
      vendorClass,
      titleClass,
      priceClass,
      cardConfig,
      cardIndex,
      staggerReveal
    } = opts;

    const primaryCls = this.escapeHtml(primaryImageClasses);
    const secondaryCls = this.escapeHtml(secondaryImageClasses);
    const vendorCls = this.escapeHtml(vendorClass || "");
    const titleCls = this.escapeHtml(titleClass || "");
    const priceCls = this.escapeHtml(priceClass || "");

    const primaryImageMarkup = product.image
      ? `<img src="${this.escapeHtml(product.image)}" alt="${this.escapeHtml(product.title)}" loading="lazy" class="${primaryCls}">`
      : `<div class="${primaryCls}"></div>`;

    const secondaryImageMarkup = product.secondaryImage
      ? `<img src="${this.escapeHtml(product.secondaryImage)}" alt="${this.escapeHtml(product.title)}" loading="lazy" fetchpriority="low" draggable="false" class="${secondaryCls}">`
      : "";

    const vendorMarkup = product.vendor
      ? `<a href="/collections/vendors?q=${encodeURIComponent(product.vendor)}" class="vendor ${vendorCls}">${this.escapeHtml(product.vendor)}</a>`
      : "";

    const priceMarkup = this.renderPriceMarkup(product, priceCls);

    const handleAttr = product.handle ? ` handle="${this.escapeHtml(product.handle)}"` : "";
    const wishlistIdAttr = product.wishlistItemId ? ` data-wishlist-item-id="${this.escapeHtml(product.wishlistItemId)}"` : "";
    const revealAttr = staggerReveal
      ? ` reveal-on-scroll="${staggerReveal}"`
      : ` reveal-on-scroll="false"`;

    const uniqueSuffix = this.cardUniqueSuffix(product, cardConfig, cardIndex);
    const showFloatingSize = this.shouldShowFloatingSizeSelector(product, cardConfig);
    const floatingSizeMarkup = showFloatingSize ? this.renderFloatingSizeSelector(product, cardConfig, uniqueSuffix) : "";
    const quickBuyMarkup = this.renderQuickBuy(product, cardConfig, uniqueSuffix, showFloatingSize);
    const swatchesMarkup = this.renderColorSwatches(product, cardConfig, uniqueSuffix);
    const swatchesOutside = swatchesMarkup && cardConfig.swatchesPosition === "outside";
    const swatchesInside = swatchesMarkup && cardConfig.swatchesPosition === "inside";

    const badgesMarkup = this.renderProductBadges(product, cardConfig);

    const wishlistBtn = product.handle
      ? `<div class="wishlist-collection-button">
        <button type="button" aria-label="Add to Wishlist" class="wkh-button wk-selected" data-product-handle="${this.escapeHtml(product.handle)}">
          ${this.heartIconHtml}
        </button>
      </div>`
      : "";

    return `
    <product-card class="product-card product-card--info-outside product-card--blends"${handleAttr}${wishlistIdAttr}${revealAttr}>
      <div class="product-card__figure">
        ${badgesMarkup}
        <a href="${this.escapeHtml(product.url)}" class="product-card__media" data-instant>
          ${primaryImageMarkup}
          ${secondaryImageMarkup}
        </a>
        ${wishlistBtn}
        ${quickBuyMarkup}
        ${floatingSizeMarkup}
      </div>
      <div class="product-card__info">
        <div class="v-stack gap-2.5 justify-items-start text-start">
          <div class="v-stack gap-1">
            ${vendorMarkup}
            <a href="${this.escapeHtml(product.url)}" class="product-title ${titleCls}" data-instant>${this.escapeHtml(product.title)}</a>
            ${priceMarkup}
          </div>
          ${swatchesOutside ? swatchesMarkup : ""}
        </div>
      </div>
      ${swatchesInside ? swatchesMarkup : ""}
    </product-card>`;
  }
}

if (!window.customElements.get("wishlist-product-grid")) {
  window.customElements.define("wishlist-product-grid", WishlistProductGrid);
}
